// src/components/KanbanView/KanbanView.jsx
import React, { useMemo, useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import PropTypes from "prop-types";
import "./KanbanView.scss";
import { can, PERM, normalizeRole, ROLES } from "../../config/roles";
import CheckList from "../CheckList/CheckList";
import AddTodoModal from "../Update/AddTodoModal/AddTodoModal";
import AddChecklistModal from "../Update/AddChecklistModal/AddChecklistModal";
import AddButton from "../AddButton";
import InfoButton from "../InfoButton/InfoButton";
import DetailsModal from "../DetailsModal/DetailsModal";
import Toast from "../Toast/Toast";
import TooltipPortal from "../TooltipPortal/TooltipPortal";
import CustomTooltip from "../CustomTooltip/CustomTooltip";
import DeleteButton from "../DeleteButton/DeleteButton";
import DeleteWarningModal from "../DeleteWarningModal/DeleteWarningModal";
import AddStageModal from "../AddStageModal/AddStageModal";
import AddStageButton from "../AddStageButton/AddStageButton";
// ----------------------------------------------------------------------
// AUTO EDGE SCROLL HOOK — hover left/right edges to move horizontal board
// ----------------------------------------------------------------------
function useEdgeAutoScroll(boardRef, opts = {}) {
  const {
    threshold = 0.2, // 20% left/right edge
    maxSpeed = 1200, // px per second
    enabled = true,
    isDragging = false,
  } = opts;

  let rafId = null;
  let lastTs = null;
  let pointerX = null;
  let rect = null;

  const stop = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    lastTs = null;
    pointerX = null;
    rect = null;
  };

  const loop = (ts) => {
    if (!enabled || isDragging) return stop();
    if (!boardRef?.current || pointerX == null) return stop();

    if (!lastTs) lastTs = ts;
    const dt = ts - lastTs;
    lastTs = ts;

    if (!rect) rect = boardRef.current.getBoundingClientRect();
    const w = rect.width;
    if (!w) return;

    const localX = pointerX - rect.left;
    const norm = localX / w;

    const leftZone = threshold;
    const rightZone = 1 - threshold;

    let direction = 0;
    let intensity = 0;

    if (norm < leftZone) {
      direction = -1;
      intensity = (leftZone - norm) / leftZone;
    } else if (norm > rightZone) {
      direction = 1;
      intensity = (norm - rightZone) / leftZone;
    } else {
      return stop();
    }

    const speed = maxSpeed * intensity;
    const delta = (speed * dt) / 1000;

    const el = boardRef.current;
    const maxScroll = el.scrollWidth - el.clientWidth;

    if (direction > 0) {
      el.scrollLeft = Math.min(maxScroll, el.scrollLeft + delta);
      if (el.scrollLeft >= maxScroll - 1) return stop();
    } else {
      el.scrollLeft = Math.max(0, el.scrollLeft - delta);
      if (el.scrollLeft <= 1) return stop();
    }

    rafId = requestAnimationFrame(loop);
  };

  const handleMove = (e) => {
    pointerX = e.clientX;
    rect = boardRef.current?.getBoundingClientRect() ?? null;
    if (!rafId) rafId = requestAnimationFrame(loop);
  };

  const handleEnter = (e) => {
    pointerX = e.clientX;
    rect = boardRef.current?.getBoundingClientRect() ?? null;
    if (!rafId) rafId = requestAnimationFrame(loop);
  };

  const handleLeave = () => stop();

  const handleBlur = () => stop();

  const enableListeners = () => {
    window.addEventListener("blur", handleBlur);
  };

  const disableListeners = () => {
    stop();
    window.removeEventListener("blur", handleBlur);
  };

  return {
    edgeHandlers: {
      onMouseMove: handleMove,
      onMouseEnter: handleEnter,
      onMouseLeave: handleLeave,
    },
    enableListeners,
    disableListeners,
    stop,
  };
}

export default function KanbanView({
  tasks = [],
  stages = [],
  onChangeTaskStage,
  onChangeTodoStage,
  onUpdateTodo,
  onUpdateTodoList,
  onToggleChecklist,
  stageChangeLoading = {},
  projectEndDate,
  handleUpdateTask,
  handleUpdateTodo,
  currentUserRole,
  currentEmpId,
  currentAccessId,
  projectMembers = [],
  onCreateTodo,
  parentSubmit,
  onCreateTodoList,
  projectId,
  onDeleteTask,
  onDeleteTodo,
  onDeleteTodoList,
  onUpdateChecklistRemark,
}) {
  const [expanded, setExpanded] = useState(true);

  const [selectedItem, setSelectedItem] = useState(null);
  const [todoModal, setTodoModal] = useState({
    open: false,
    taskId: null,
    stagePositionId: null,
  });
  const [checkModal, setCheckModal] = useState({ open: false, toDoId: null });
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "info",
  });

  // in-flight trackers to keep optimistic items from being overwritten by fresh props
  const inFlightTaskStageRef = React.useRef(new Set()); // holds taskId strings
  const inFlightTodoStageRef = React.useRef(new Set()); // holds todoId strings
  // desired target stage while waiting for server to catch up
  // eslint-disable-next-line no-unused-vars
  const pendingTaskStageRef = React.useRef(new Map()); // taskId -> desired stagePositionId
  // eslint-disable-next-line no-unused-vars
  const pendingTodoStageRef = React.useRef(new Map()); // todoId  -> desired stagePositionId
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'task'|'todo'|'checklist', id }
  const [addStageOpen, setAddStageOpen] = useState(false);
  const role = normalizeRole(currentUserRole);

  // ---------- Stage change remark prompt ----------
  // payload shape:
  //   { kind: "task", taskId, newStageId, stageName, isFinal, previous }
  //   { kind: "todo", todoId, newStageId, previous }
  const [stageRemarkPrompt, setStageRemarkPrompt] = useState(null);
  const [stageRemarkText, setStageRemarkText] = useState("");

  // ---------- Checklist remark / approve ----------
  const [checklistRemarkPrompt, setChecklistRemarkPrompt] = useState(null); // { toDoListId, title, completion }
  const [checklistRemarkText, setChecklistRemarkText] = useState("");

  const openChecklistRemarkPrompt = (li) => {
    if (!li) return;
    setChecklistRemarkText(li.remark || "");
    setChecklistRemarkPrompt({
      toDoListId: li.toDoListId ?? li.id,
      title: li.toDoListTitle ?? "Checklist item",
      completion: !!li.completion,
    });
  };

  const cancelChecklistRemarkPrompt = () => {
    setChecklistRemarkPrompt(null);
    setChecklistRemarkText("");
  };

  const confirmChecklistRemarkPrompt = async () => {
    if (!checklistRemarkPrompt) return;
    const { toDoListId, completion } = checklistRemarkPrompt;
    try {
      await onUpdateChecklistRemark?.(toDoListId, {
        completionFlag: completion,
        remark: checklistRemarkText,
      });
    } finally {
      setChecklistRemarkPrompt(null);
      setChecklistRemarkText("");
    }
  };

  const handleApproveChecklistItem = (li) => {
    const toDoListId = li?.toDoListId ?? li?.id;
    if (!toDoListId) return;
    onUpdateChecklistRemark?.(toDoListId, {
      completionFlag: true,
      remark: li?.remark ?? "",
    });
  };

  const openStageRemarkPrompt = (payload) => {
    setStageRemarkText("");
    setStageRemarkPrompt(payload);
  };

  const commitTaskStageRemark = async (
    taskId,
    newStageId,
    stageName,
    isFinal,
    previous,
    remark,
  ) => {
    const trimmedRemark = remark && remark.trim() ? remark.trim() : undefined;
    try {
      if (can(role, PERM.TASK_UPDATE)) {
        await onChangeTaskStage(
          taskId,
          newStageId,
          stageName,
          isFinal,
          trimmedRemark,
        );
      }
    } catch (e) {
      setLocalTasks(previous);
      triggerShake(taskId);
      const serverMsg =
        e?.response?.data?.message || e?.message || "Failed to move Task";
      setToast?.({ show: true, type: "error", message: serverMsg });
    } finally {
      inFlightTaskStageRef.current.delete(String(taskId));
    }
  };

  const commitTodoStageRemark = async (
    todoId,
    newStageId,
    previous,
    remark,
  ) => {
    const trimmedRemark = remark && remark.trim() ? remark.trim() : undefined;
    try {
      const maybePromise = onChangeTodoStage(todoId, newStageId, trimmedRemark);
      if (maybePromise && typeof maybePromise.then === "function") {
        await maybePromise;
      }
    } catch (err) {
      const serverMsg =
        err?.response?.data?.message || err?.message || "Failed to move ToDo";
      if (previous) setLocalTasks(previous);
      triggerShake(todoId);
      if (typeof setToast === "function")
        setToast({ show: true, type: "error", message: serverMsg });
    } finally {
      inFlightTodoStageRef.current.delete(String(todoId));
    }
  };

  const confirmStageRemark = () => {
    if (!stageRemarkPrompt) return;
    const { kind, taskId, todoId, newStageId, stageName, isFinal, previous } =
      stageRemarkPrompt;
    if (kind === "task") {
      commitTaskStageRemark(
        taskId,
        newStageId,
        stageName,
        isFinal,
        previous,
        stageRemarkText,
      );
    } else if (kind === "todo") {
      commitTodoStageRemark(todoId, newStageId, previous, stageRemarkText);
    }
    setStageRemarkPrompt(null);
    setStageRemarkText("");
  };

  const cancelStageRemark = () => {
    if (!stageRemarkPrompt) return;
    const { kind, taskId, todoId, previous } = stageRemarkPrompt;
    if (previous) setLocalTasks(previous);
    if (kind === "task") inFlightTaskStageRef.current.delete(String(taskId));
    if (kind === "todo") inFlightTodoStageRef.current.delete(String(todoId));
    setStageRemarkPrompt(null);
    setStageRemarkText("");
  };

  // The horizontal board container
  const boardRef = React.useRef(null);
  // useEffect(() => {
  //   const board = boardRef.current;
  //   const bar = document.querySelector(".kanban-fixed-scrollbar");
  //   if (!board || !bar) return;

  //   const sync = () => {
  //     bar.scrollLeft = board.scrollLeft;
  //   };

  //   board.addEventListener("scroll", sync, { passive: true });
  //   return () => board.removeEventListener("scroll", sync);
  // }, []);

  // If you have dragging state, use it. If not, set to false.
  const [isDragging, setIsDragging] = useState(false);

  const sortedStages = React.useMemo(() => {
    const getStagePosition = (stage) => {
      const list = stage.stagePositionResponseDTOList || [];
      const nums = (list || [])
        .map((p) => Number(p.position ?? p.pos ?? NaN))
        .filter((n) => Number.isFinite(n));
      return nums.length ? Math.min(...nums) : Number.POSITIVE_INFINITY;
    };
    return (Array.isArray(stages) ? stages.slice() : []).sort((a, b) => {
      const pa = getStagePosition(a),
        pb = getStagePosition(b);
      if (pa === pb)
        return String(a.stageName || "").localeCompare(
          String(b.stageName || ""),
        );
      return pa - pb;
    });
  }, [stages]);

  // ---- Column stretch helper ----
  // min width in px that a column should have in scroll mode
  const MIN_COLUMN_WIDTH = 320; // tweak as needed (300-360 typical)

  const [stretchColumns, setStretchColumns] = React.useState(false);

  // Compute whether to stretch columns to fill viewport
  React.useEffect(() => {
    if (!boardRef || !boardRef.current) {
      setStretchColumns(false);
      return;
    }

    const calc = () => {
      const container = boardRef.current;
      if (!container) return setStretchColumns(false);

      // number of visible columns (sortedStages is your column array)
      const cols = (sortedStages || []).length || 0;
      const containerWidth =
        container.clientWidth || document.documentElement.clientWidth;
      const required = cols * MIN_COLUMN_WIDTH;

      // If required width <= container width -> stretch (no need to scroll)
      setStretchColumns(required <= containerWidth);
    };

    calc();

    // Recalc on resize and when stages change
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [boardRef, sortedStages]);

  const { enableListeners, disableListeners } = useEdgeAutoScroll(boardRef, {
    threshold: 0.2, // 20% edges
    maxSpeed: 1200,
    enabled: false,
    isDragging,
  });

  // Set up window-level listeners once on mount. enableListeners/
  // disableListeners are re-created on every render by the hook (it isn't
  // memoized), so adding them as deps here would re-run this effect on
  // every render instead of just mount/unmount — intentionally omitted.
  useEffect(() => {
    enableListeners();
    return () => disableListeners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local tasks for optimistic updates
  const [localTasks, setLocalTasks] = useState(tasks);
  const [shakeIds, setShakeIds] = useState([]); // array of string ids
  // at top of KanbanView component, after props/states

  useEffect(() => {
    setLocalTasks((prev) => {
      // if no previous, just set server value
      if (!prev || prev.length === 0) return tasks || [];

      // build map for prev
      const prevById = new Map(prev.map((t) => [String(t.taskId ?? t.id), t]));

      // for each server task, if it's in-flight then keep prev's version (optimistic),
      // otherwise take the server version to keep authoritative data in sync.
      return (tasks || []).map((serverT) => {
        const id = String(serverT.taskId ?? serverT.id ?? "");
        if (inFlightTaskStageRef.current.has(id))
          return prevById.get(id) ?? serverT;

        // for todos, merge per-task keeping in-flight todos
        const prevTask = prevById.get(id);
        if (!prevTask) return serverT;

        const prevTodosById = new Map(
          (prevTask.todoRequestDTOList || []).map((td) => [
            String(td.toDoId ?? td.id),
            td,
          ]),
        );
        const mergedTodos = (serverT.todoRequestDTOList || []).map((st) => {
          const tid = String(st.toDoId ?? st.id);
          if (inFlightTodoStageRef.current.has(tid))
            return prevTodosById.get(tid) ?? st;
          return st;
        });
        return { ...serverT, todoRequestDTOList: mergedTodos };
      });
    });
  }, [tasks]);
  // seed expanded map so all tasks are open by default (only when empty)
  useEffect(() => {
    // don't overwrite user toggles — only seed when expanded is empty
    if (Object.keys(expanded || {}).length > 0) return;

    const taskList = localTasks && localTasks.length ? localTasks : tasks || [];
    const seeded = {};
    taskList.forEach((t) => {
      const id = String(t.taskId ?? t.id ?? "");
      if (id) seeded[id] = true;
    });

    if (Object.keys(seeded).length > 0) {
      setExpanded(seeded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localTasks, tasks]);

  const triggerShake = (id, ms = 600) => {
    const sid = String(id);
    setShakeIds((s) => (s.includes(sid) ? s : [...s, sid]));
    setTimeout(() => {
      setShakeIds((s) => s.filter((x) => x !== sid));
    }, ms);
  };

  const toggleExpand = (taskId) =>
    setExpanded((prev) => ({ ...prev, [taskId]: !prev[taskId] }));

  // ---------- helpers ----------
  const isTaskOwner = (task) =>
    String(task?.taskOwnerId ?? task?.ownerId ?? task?.createdEmpId ?? "") ===
    String(currentEmpId);

  const isTodoAssignee = (todo) =>
    String(todo?.assignedEmpId ?? todo?.assigneeId ?? todo?.empId ?? "") ===
    String(currentEmpId);

  const canEditTask = (task) => {
    const r = role;
    const privileged = r === ROLES.CREATOR || r === ROLES.APPROVER;
    const owner = isTaskOwner(task);
    return (privileged || owner) && can(r, PERM.TASK_UPDATE);
  };

  const canChangeTodoStage = (todo, parentTask) => {
    const r = role;
    if (r === ROLES.DOER && isTodoAssignee(todo)) return true;
    const privileged = r === ROLES.CREATOR || r === ROLES.APPROVER;
    const owner = isTaskOwner(parentTask ?? todo?.__parent ?? {});
    return (privileged || owner) && can(r, PERM.TODO_UPDATE);
  };
  // ---------- NEW: delete permission helpers (mirrors TableView gating) ----------
  const getTaskOwnerId = (task) =>
    task?.taskOwnerId ??
    task?.ownerId ??
    task?.taskOwnerEmpId ??
    task?.createdEmpId ??
    task?.createdBy ??
    task?.assignedEmpId ??
    null;

  const getTodoOwnerId = (todo) =>
    todo?.assignedEmpId ??
    todo?.assignedToEmpId ??
    todo?.assigneeId ??
    todo?.empId ??
    todo?.userId ??
    todo?.assignedTo ??
    todo?.ownerId ??
    null;

  const canDeleteTaskFor = (task) => {
    const r = role;
    if (!can(r, PERM.TASK_DELETE)) return false;
    const ownerId = getTaskOwnerId(task);
    const isOwner = String(ownerId) === String(currentEmpId);
    if (r === ROLES.CREATOR || r === ROLES.APPROVER) return true;
    if (r === ROLES.DOER && isOwner) return true;
    return false;
  };

  // eslint-disable-next-line no-unused-vars
  const canDeleteTodoFor = (todo, parentTask = null) => {
    const r = role;
    if (!can(r, PERM.TODO_DELETE)) return false;
    const todoOwnerId = getTodoOwnerId(todo);
    const parentOwnerId = getTaskOwnerId(parentTask ?? {});
    const isTodoOwner = String(todoOwnerId) === String(currentEmpId);
    const isTaskOwner = String(parentOwnerId) === String(currentEmpId);
    if (r === ROLES.CREATOR || r === ROLES.APPROVER) return true;
    if (r === ROLES.DOER && (isTodoOwner || isTaskOwner)) return true;
    return false;
  };

  // eslint-disable-next-line no-unused-vars
  const canDeleteChecklistFor = (listItem, parentTodo, parentTask) => {
    const r = role;
    if (!can(r, PERM.TODOLIST_DELETE) && !can(r, PERM.TODOLIST_UPDATE))
      return false;
    const listOwnerId =
      listItem?.empId ?? listItem?.assignedEmpId ?? listItem?.ownerId ?? null;
    const todoOwnerId = getTodoOwnerId(parentTodo ?? {});
    const taskOwnerId = getTaskOwnerId(parentTask ?? {});
    const isListOwner = String(listOwnerId) === String(currentEmpId);
    const isTodoOrTaskOwner =
      String(todoOwnerId) === String(currentEmpId) ||
      String(taskOwnerId) === String(currentEmpId);
    if (r === ROLES.CREATOR || r === ROLES.APPROVER) return true;
    if (r === ROLES.DOER && (isListOwner || isTodoOrTaskOwner)) return true;
    return false;
  };
  // ---------- END delete permission helpers ----------

  // ---- completion helpers ----
  const isChecklistItemDone = (li) =>
    !!(li?.completion ?? li?.completionFlag) ||
    (li?.status && String(li.status).toUpperCase() === "DONE");

  const isTodoDone = (todo) => {
    const lists = todo?.toDoListResponseDTOList || [];
    // Require at least one item and all must be done
    return lists.length > 0 && lists.every(isChecklistItemDone);
  };

  const isTaskCompletable = (task) => {
    const todos = task?.todoRequestDTOList || [];
    // All todos must be "done" (their checklists all done)
    return todos.length > 0 && todos.every(isTodoDone);
  };

  const calcProgress = (start, end) => {
    if (!start || !end) return { progress: 0, isOverdue: false };
    const s = new Date(start);
    const e = new Date(end);
    const now = new Date();
    const total = e - s;
    const elapsed = now - s;
    if (total <= 0) return { progress: 0, isOverdue: now > e };
    let pct = elapsed / total;
    pct = Math.min(Math.max(pct, 0), 1);
    return { progress: pct, isOverdue: now > e };
  };
  // global color counter — resets on mount
  // Stable avatar color allocator (persists across renders)
  const avatarColorMapRef = React.useRef(new Map());
  const avatarColorCounterRef = React.useRef(1);
  const MAX_AVATAR_COLORS = 5;

  const getAvatarColorFor = (key) => {
    if (key === undefined || key === null) {
      // fallback deterministic key for anonymous entries
      key = "__anon__";
    }
    const map = avatarColorMapRef.current;
    if (map.has(String(key))) return map.get(String(key));

    // allocate next color index cyclically
    const next = avatarColorCounterRef.current;
    const colorIndex = next; // 1..MAX_AVATAR_COLORS
    avatarColorCounterRef.current =
      avatarColorCounterRef.current >= MAX_AVATAR_COLORS
        ? 1
        : avatarColorCounterRef.current + 1;

    map.set(String(key), colorIndex);
    return colorIndex;
  };

  // eslint-disable-next-line no-unused-vars
  const stageOptions = useMemo(() => {
    const byId = new Map();
    for (const s of sortedStages || []) {
      for (const pos of s.stagePositionResponseDTOList || []) {
        const id = String(pos.stagePositionId ?? pos.id ?? "");
        if (!id || byId.has(id)) continue;
        const label =
          s.stageName ??
          s.stageTitle ??
          s.title ??
          `Stage ${pos.stagePositionId}`;
        const flag = s.stageFlag ?? s.flag ?? s.stageFlagType ?? null;
        byId.set(id, { value: pos.stagePositionId, label, flag });
      }
    }
    return Array.from(byId.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages]);

  const handleTaskDragEnd = async (result) => {
    if (!result?.destination) return;
    if (
      result.source.droppableId === result.destination.droppableId &&
      result.source.index === result.destination.index
    )
      return;

    const taskId = Number(
      String(result.draggableId ?? "").replace(/^task-/, ""),
    );
    if (Number.isNaN(taskId)) return;

    const newStageId = Number(result.destination.droppableId);
    const task = (localTasks || []).find(
      (t) => Number(t.taskId ?? t.id) === taskId,
    );
    if (!task) return;
    if (!canEditTask(task)) return;

    // find stage meta
    const stage = stages.find((s) =>
      s.stagePositionResponseDTOList?.some(
        (pos) => Number(pos.stagePositionId) === newStageId,
      ),
    );
    const stageName = stage?.stageName ?? "";
    const flag = stage?.stageFlag ?? stage?.flag ?? "";
    const isFinal = flag && String(flag).toLowerCase().includes("final");

    // ---- CLIENT GUARD: block moving task to final unless all children are complete
    if (isFinal && !isTaskCompletable(task)) {
      setToast?.({
        show: true,
        type: "info",
        message:
          "Cannot move Task to Final — all ToDos and their checklists must be DONE.",
      });
      triggerShake(taskId);
      return; // stop here; no optimistic update, no API call
    }

    // ---- OPTIMISTIC UPDATE
    const previous = localTasks;
    inFlightTaskStageRef.current.add(String(taskId));
    setLocalTasks((prev) =>
      (prev || []).map((t) =>
        Number(t.taskId ?? t.id) === taskId
          ? { ...t, stagePositionId: newStageId }
          : t,
      ),
    );

    // ask for an optional remark before committing the stage change
    openStageRemarkPrompt({
      kind: "task",
      taskId,
      newStageId,
      stageName,
      isFinal,
      previous,
    });
  };

  // eslint-disable-next-line no-unused-vars
  const fmt = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date)) return String(d);
    return date.toLocaleDateString();
  };

  const canSeeFullTask = (task) => {
    const r = normalizeRole(currentUserRole);
    const owner =
      String(task?.taskOwnerId ?? task?.ownerId ?? task?.createdEmpId ?? "") ===
      String(currentEmpId);
    if (r === ROLES.CREATOR || r === ROLES.APPROVER || owner) return true;
    if (r === ROLES.DOER) {
      const isAssigned = (task.todoRequestDTOList || []).some(
        (td) =>
          String(td.assignedEmpId ?? td.assigneeId ?? td.empId ?? "") ===
          String(currentEmpId),
      );
      // DOERs see compact ToDos when they are assigned; full tasks when not assigned
      return !isAssigned;
    }
    return false;
  };

  // ---------- DragDropContext handler (uses prefixes) ----------
  const onDragEndHandler = async (result) => {
    try {
      if (!result || !result.destination) return;
      const { draggableId } = result;
      if (typeof draggableId !== "string") return;

      // TASK
      if (draggableId.startsWith("task-") || /^task-?/.test(draggableId)) {
        return handleTaskDragEnd(result);
      }

      // TODO
      // TODO branch — optimistic update + rollback
      if (draggableId.startsWith("todo-")) {
        const todoIdRaw = draggableId.replace(/^todo-/, "");
        const todoIdNum = Number(todoIdRaw);
        const newStageId = Number(result.destination.droppableId);

        // find todo from localTasks (and its parent)
        const todo = (localTasks || [])
          .flatMap((t) =>
            (t.todoRequestDTOList || []).map((td) => ({ ...td, __parent: t })),
          )
          .find(
            (td) =>
              String(td.toDoId ?? td.id) === String(todoIdRaw) ||
              String(td.toDoId ?? td.id) === String(todoIdNum),
          );

        if (!todo) {
          console.warn("Todo not found for", todoIdRaw);
          return;
        }

        if (!canChangeTodoStage(todo, todo.__parent)) {
          console.warn("Not allowed to change todo stage", todoIdRaw);
          return;
        }

        if (typeof onChangeTodoStage !== "function") {
          console.error("onChangeTodoStage handler not provided");
          return;
        }

        // --- client-side final-stage guard: prevent server rejection & flicker ---
        const stageObj = stages.find((s) =>
          s.stagePositionResponseDTOList?.some(
            (p) => Number(p.stagePositionId) === newStageId,
          ),
        );
        const stageFlag = stageObj?.stageFlag ?? stageObj?.flag ?? "";
        const isFinalStage =
          stageFlag && String(stageFlag).toLowerCase().includes("final");

        if (isFinalStage && !isTodoDone(todo)) {
          setToast?.({
            show: true,
            type: "info",
            message:
              "Cannot move ToDo to Final — all checklist items must be DONE.",
          });
          triggerShake(todoIdNum);
          return; // stop here, don't call API
        }

        // OPTIMISTIC UPDATE
        const previous = localTasks;
        inFlightTodoStageRef.current.add(String(todoIdNum));
        setLocalTasks((prev) =>
          (prev || []).map((t) => {
            const parentId = todo.__parent?.taskId ?? todo.__parent?.id;
            if (String(t.taskId ?? t.id) !== String(parentId)) return t;
            return {
              ...t,
              todoRequestDTOList: (t.todoRequestDTOList || []).map((td) =>
                String(td.toDoId ?? td.id) === String(todoIdNum)
                  ? { ...td, stagePositionId: newStageId }
                  : td,
              ),
            };
          }),
        );

        // ask for an optional remark before committing the stage change
        openStageRemarkPrompt({
          kind: "todo",
          todoId: Number(todoIdNum),
          newStageId,
          previous,
        });

        return;
      }

      console.warn("Unknown draggableId format:", draggableId);
    } catch (err) {
      console.error("Drag end handler crashed:", err);
    }
  };

  const openDetailsModal = (item, parentTask = null) => {
    const it = { ...(item ?? {}) };

    // DEBUG: show the actual object and keys so we can find the owner field
    console.debug("openDetailsModal: item:", it);
    console.debug("openDetailsModal: keys:", Object.keys(it));

    // Checklist item (ToDoList) branch — keep existing semantics but include parent ref
    if (it?.toDoListId || (it?.id && it?.toDoListTitle)) {
      setSelectedItem({
        ...it,
        isChecklist: true,
        __parent: parentTask ?? it.__parent ?? null,
      });
      return;
    }

    // derive parent task owner using same fallback chain TableView uses
    const parentOwner =
      parentTask?.taskOwnerId ??
      parentTask?.ownerId ??
      it?.parentTaskOwnerId ??
      it?.parentOwnerId ??
      (it?.__parent && (it.__parent.taskOwnerId ?? it.__parent.ownerId)) ??
      null;

    // helper to find todo owner from common possible fields
    const getTodoOwnerFrom = (o) =>
      o?.assignedToEmpId ??
      o?.assignedEmpId ??
      o?.assigneeId ??
      o?.assigned_to_emp_id ??
      o?.assignee_emp_id ??
      o?.empId ??
      o?.userId ??
      o?.assignedTo ??
      o?.ownerId ??
      null;

    // ToDo branch: normalize to `userId` (TableView expects this)
    if (it?.toDoId || it?.toDoId === 0) {
      const todoOwnerId = getTodoOwnerFrom(it);
      setSelectedItem({
        ...it,
        // TableView uses `userId` for ToDo owner — match that
        userId: todoOwnerId,
        parentTaskOwnerId: parentOwner,
        __parent: parentTask ?? it.__parent ?? null,
      });
      return;
    }

    // Task branch: normalize to `taskOwnerId`
    if (it?.taskId || it?.id) {
      const taskOwnerId =
        it.taskOwnerId ??
        it.ownerId ??
        it.taskOwnerEmpId ??
        it.createdEmpId ??
        null;
      setSelectedItem({
        ...it,
        taskOwnerId,
        parentTaskOwnerId: parentOwner,
        __parent: parentTask ?? it.__parent ?? null,
      });
      return;
    }

    // Fallback: just set item as-is
    setSelectedItem({
      ...it,
      parentTaskOwnerId: parentOwner,
      __parent: parentTask ?? it.__parent ?? null,
    });
  };

  // ---------- Render ----------
  return (
    <div className="kanban-view555">
      <div
        ref={boardRef}
        className={`kanban-scroll-container ${
          stretchColumns ? "kanban--stretch" : "kanban--scroll"
        }`}
        // {...edgeHandlers}
        // onWheel={() => stopEdgeScroll()}
      >
        <DragDropContext
          onDragStart={() => {
            setIsDragging(true);
            document.body.style.overflow = "hidden";
          }}
          onDragEnd={(result) => {
            setIsDragging(false);
            document.body.style.overflow = "";
            onDragEndHandler(result);
          }}
        >
          {sortedStages.map((stage) => {
            const isLastColumn = (() => {
              // compare position of this stage to max
              const pos = (stage.stagePositionResponseDTOList || [])[0];
              const pnum = Number(pos?.position ?? pos?.pos ?? NaN);
              let max = -Infinity;
              for (const s2 of sortedStages) {
                for (const p2 of s2.stagePositionResponseDTOList || []) {
                  const vv = Number(p2.position ?? p2.pos ?? NaN);
                  if (Number.isFinite(vv) && vv > max) max = vv;
                }
              }
              return pnum === max;
            })();

            const stageId =
              stage.stagePositionResponseDTOList?.[0]?.stagePositionId;
            if (!stageId) return null;

            // Build a flat ordered list of items for this column so droppable children have consecutive indexes:
            //  - tasks that should show full view (type: 'task')
            //  - todos assigned to current user for compact (type: 'todo')
            const tasksInStage = (localTasks || []).filter(
              (t) => Number(t.stagePositionId) === Number(stageId),
            );

            // Build columnItems with consecutive indexes:
            // 1) tasks that should render full (task card)
            // 2) todos in this stage that should be shown as compact cards
            const columnItems = [];

            for (const t of tasksInStage) {
              if (canSeeFullTask(t)) {
                columnItems.push({ type: "task", item: t });
              }
            }

            const todosInStage = (localTasks || [])
              .flatMap((t) =>
                (t.todoRequestDTOList || []).map((td) => ({
                  ...td,
                  __parent: t,
                })),
              )
              .filter(
                (td) =>
                  Number(td.stagePositionId) === Number(stageId) &&
                  String(
                    td.assignedEmpId ?? td.assigneeId ?? td.empId ?? "",
                  ) === String(currentEmpId) &&
                  !canSeeFullTask(td.__parent),
              );

            for (const td of todosInStage) {
              columnItems.push({ type: "todo", item: td, parent: td.__parent });
            }
            const normalizedFlag = (stage.stageFlag || "")
              .trim()
              .toLowerCase()
              .replace(/\s+/g, ""); // e.g. "On Hold" → "onhold"

            const stageClass = `kanban-stagecard kanban-stagecard-${normalizedFlag}`;
            return (
              <Droppable droppableId={String(stageId)} key={stageId}>
                {(provided) => (
                  <div
                    className="kanban-column555"
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    <div
                      className={stageClass}
                      // style={{
                      //   display: "flex",
                      //   alignItems: "center",
                      //   justifyContent: "space-between",
                      // }}
                    >
                      <CustomTooltip content={stage.stageName || ""}>
                        <span className="stage-name-text truncate">
                          {stage.stageName}
                        </span>
                      </CustomTooltip>
                      <span className="stage-count-badge">
                        {columnItems.length}
                      </span>
                      {isLastColumn && can(role, PERM.TASK_UPDATE) && (
                        <AddStageButton onClick={() => setAddStageOpen(true)} />
                      )}
                    </div>

                    {columnItems.map((colItem, index) => {
                      if (colItem.type === "task") {
                        const task = colItem.item;
                        const taskId = task.taskId ?? task.id;
                        const { progress, isOverdue } = calcProgress(
                          task.startDate,
                          task.endDate,
                        );

                        return (
                          <Draggable
                            key={`task-${taskId}`}
                            draggableId={`task-${taskId}`}
                            index={index}
                            isDragDisabled={!canEditTask(task)}
                          >
                            {(dragProvided, snapshot) => (
                              <div
                                className={`kanban-task555 ${
                                  snapshot.isDragging ? "dragging" : ""
                                } ${
                                  shakeIds.includes(String(taskId))
                                    ? "shake"
                                    : ""
                                }`}
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                              >
                                {/* Full Task Layout */}
                                <div className="task-header555">
                                  <button
                                    className={`toggle-btn555 ${
                                      expanded[taskId] ? "open" : ""
                                    }`}
                                    onClick={() => toggleExpand(taskId)}
                                    type="button"
                                    aria-expanded={!!expanded[taskId]}
                                    aria-controls={`task-details-${taskId}`}
                                    aria-label={
                                      expanded[taskId]
                                        ? "Collapse task details"
                                        : "Expand task details"
                                    }
                                  >
                                    ▸
                                  </button>

                                  <CustomTooltip content={task.taskTitle || ""}>
                                    <span className="title555 truncate">
                                      {task.taskTitle}
                                    </span>
                                  </CustomTooltip>

                                  {can(role, PERM.TODO_CREATE) && (
                                    <AddButton
                                      size={28}
                                      title="Todo"
                                      tooltip="Create New Task"
                                      label="ToDo"
                                      onClick={() =>
                                        setTodoModal({
                                          open: true,
                                          taskId: taskId,
                                          stagePositionId: stageId,
                                        })
                                      }
                                    />
                                  )}

                                  <span
                                    className="info-btn555-wrap"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openDetailsModal(task)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        openDetailsModal(task);
                                      }
                                    }}
                                  >
                                    <InfoButton />
                                  </span>
                                  {/* Task Delete (gated like TableView) */}
                                  <TooltipPortal
                                    label={
                                      canDeleteTaskFor(task)
                                        ? "Delete this Task (cannot be undone)"
                                        : "You don't have permission to delete this Task"
                                    }
                                    offsetY={28}
                                  >
                                    {canDeleteTaskFor(task) ? (
                                      <DeleteButton
                                        size={42}
                                        onClick={() =>
                                          setDeleteTarget({
                                            type: "task",
                                            id: task.taskId ?? task.id,
                                          })
                                        }
                                      />
                                    ) : (
                                      // Keep DeleteButton unchanged — show a disabled placeholder instead when not allowed
                                      <button
                                        type="button"
                                        disabled
                                        title="You don't have permission to delete this Task"
                                        className="disabled-delete-btn"
                                        style={{
                                          background: "transparent",
                                          border: "none",
                                          cursor: "not-allowed",
                                          opacity: 0.6,
                                          padding: 0,
                                          marginLeft: 8,
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                        aria-hidden="true"
                                      >
                                        {/* Small trash SVG to match spacing */}
                                        <svg
                                          width="18"
                                          height="18"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          aria-hidden
                                        >
                                          <path
                                            d="M3 6h18"
                                            stroke="#999"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                          ></path>
                                          <path
                                            d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6"
                                            stroke="#999"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          ></path>
                                          <path
                                            d="M10 11v6M14 11v6"
                                            stroke="#999"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                          ></path>
                                        </svg>
                                      </button>
                                    )}
                                  </TooltipPortal>

                                  {/* <TooltipPortal label="Delete this ToDo (cannot be undone)" offsetY={28}>
   <DeleteButton onClick={() => onDeleteTodo?.(todo?.toDoId ?? todo?.id)} />

  </TooltipPortal> */}

                                  {stageChangeLoading[taskId] && (
                                    <span className="saving555">…</span>
                                  )}
                                </div>

                                <div className="task-meta555">
                                  {task.taskOwnerName && (
                                    <div className="owner-badge">
                                      <span className="owner-avatar">
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="#1877ebff"
                                        >
                                          <path
                                            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 
          1.79-4 4 1.79 4 4 4zm0 2c-2.67 
          0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                                          />
                                        </svg>
                                      </span>
                                      <span className="owner-name">
                                        {task.taskOwnerName}
                                      </span>
                                    </div>
                                  )}

                                  {task.priority && (
                                    <span
                                      className={`badge555 ${String(
                                        task.priority || "",
                                      ).toLowerCase()}`}
                                    >
                                      {task.priority}
                                    </span>
                                  )}
                                </div>

                                {task.startDate && task.endDate && (
                                  <div
                                    className={`timeline-bar555 ${
                                      isOverdue ? "overdue" : ""
                                    }`}
                                    title={`Start: ${
                                      task.startDate || "-"
                                    } | End: ${task.endDate || "-"}`}
                                  >
                                    <div
                                      className="timeline-fill555"
                                      style={{ width: `${progress * 100}%` }}
                                    />
                                  </div>
                                )}

                                <div
                                  id={`task-details-${taskId}`}
                                  className={`task-details555 ${
                                    expanded[taskId] ? "open" : ""
                                  }`}
                                >
                                  {(task.todoRequestDTOList || []).map(
                                    (todo) => {
                                      const todoId = todo.toDoId ?? todo.id;
                                      const canChecklistUpdate = can(
                                        role,
                                        PERM.TODOLIST_UPDATE,
                                      );
                                      return (
                                        <div
                                          key={todoId}
                                          className="todo-item555"
                                        >
                                          <div className="todo-header555">
                                            <CustomTooltip
                                              content={todo.toDoTitle || ""}
                                            >
                                              <span className="todo-title555 truncate">
                                                {todo.toDoTitle}
                                              </span>
                                            </CustomTooltip>
                                            {todo.remark && (
                                              <CustomTooltip
                                                content={todo.remark}
                                              >
                                                <span
                                                  className="todo-remark-icon"
                                                  style={{
                                                    marginLeft: 6,
                                                    fontSize: 12,
                                                    color: "#6b7280",
                                                    cursor: "help",
                                                  }}
                                                  title="Has remark"
                                                >
                                                  📝
                                                </span>
                                              </CustomTooltip>
                                            )}
                                            {todo.assignedEmpName ? (
                                              <div className="owner-badge">
                                                <span className="owner-avatar">
                                                  <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="#1877ebff"
                                                  >
                                                    <path
                                                      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 
          1.79-4 4 1.79 4 4 4zm0 2c-2.67 
          0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                                                    />
                                                  </svg>
                                                </span>
                                                <span className="owner-name">
                                                  {todo.assignedEmpName}
                                                </span>
                                              </div>
                                            ) : (
                                              <span className="muted555 sm">
                                                —
                                              </span>
                                            )}

                                            <span
                                              className="info-btn555-wrap"
                                              role="button"
                                              tabIndex={0}
                                              onClick={() =>
                                                openDetailsModal(todo, task)
                                              }
                                              onKeyDown={(e) => {
                                                if (
                                                  e.key === "Enter" ||
                                                  e.key === " "
                                                ) {
                                                  e.preventDefault();
                                                  openDetailsModal(todo, task);
                                                }
                                              }}
                                            >
                                              <InfoButton />
                                            </span>
                                            <TooltipPortal
                                              label="Delete this ToDo (cannot be undone)"
                                              offsetY={28}
                                            >
                                              <DeleteButton
                                                size={45}
                                                onClick={() =>
                                                  setDeleteTarget({
                                                    type: "todo",
                                                    id:
                                                      todo?.toDoId ?? todo?.id,
                                                  })
                                                }
                                              />
                                            </TooltipPortal>

                                            {/* Add Checklist icon below all todos */}
                                            {can(
                                              role,
                                              PERM.TODOLIST_UPDATE,
                                            ) && (
                                              <AddButton
                                                size={26}
                                                tooltip="Add checklist item"
                                                ariaLabel={`Add checklist to todo ${todoId}`}
                                                onClick={(e) => {
                                                  e.stopPropagation(); // ✅ safety (even if header click is removed)
                                                  setCheckModal({
                                                    open: true,
                                                    toDoId: todoId,
                                                  });
                                                }}
                                              />
                                            )}
                                          </div>
                                          {canChangeTodoStage(todo, task) && (
                                            <div className="todo-stages555">
                                              <span className="label">
                                                Stages:
                                              </span>
                                              {stages.flatMap((s) =>
                                                (
                                                  s.stagePositionResponseDTOList ||
                                                  []
                                                ).map((pos) => {
                                                  const isActive =
                                                    String(
                                                      todo.stagePositionId,
                                                    ) ===
                                                    String(pos.stagePositionId);
                                                  const isFinal = String(
                                                    s.stageFlag || "",
                                                  )
                                                    .toLowerCase()
                                                    .includes("final");
                                                  const disabled =
                                                    isFinal &&
                                                    !isTodoDone(todo);

                                                  return (
                                                    <button
                                                      key={pos.stagePositionId}
                                                      className={`stage-badge555 ${
                                                        isActive ? "active" : ""
                                                      }`}
                                                      disabled={disabled}
                                                      title={
                                                        disabled
                                                          ? "Complete all checklist items before moving to Final stage"
                                                          : `Move ToDo to ${s.stageName}`
                                                      }
                                                      onClick={() =>
                                                        !disabled &&
                                                        openStageRemarkPrompt({
                                                          kind: "todo",
                                                          todoId:
                                                            todo.toDoId ??
                                                            todo.id,
                                                          newStageId:
                                                            pos.stagePositionId,
                                                          previous: null,
                                                        })
                                                      }
                                                    >
                                                      {s.stageName}
                                                    </button>
                                                  );
                                                }),
                                              )}
                                            </div>
                                          )}
                                          {/* --- ToDo Stages Pills (for compact view, no gating) ---
                                      <div className="todo-stages555">
                                        <span className="label">Stages:</span>
                                        {stages.flatMap((s) =>
                                          (
                                            s.stagePositionResponseDTOList || []
                                          ).map((pos) => {
                                            const isActive =
                                              String(todo.stagePositionId) ===
                                              String(pos.stagePositionId);
                                            const isFinal = String(
                                              s.stageFlag || ""
                                            )
                                              .toLowerCase()
                                              .includes("final");
                                            const disabled =
                                              isFinal && !isTodoDone(todo);

                                            return (
                                              <button
                                                key={pos.stagePositionId}
                                                className={`stage-badge555 ${
                                                  isActive ? "active" : ""
                                                }`}
                                                disabled={disabled}
                                                title={
                                                  disabled
                                                    ? "Complete all checklist items before moving to Final stage"
                                                    : `Move ToDo to ${s.stageName}`
                                                }
                                                onClick={() =>
                                                  !disabled &&
                                                  onChangeTodoStage(
                                                    todo.toDoId ?? todo.id,
                                                    pos.stagePositionId
                                                  )
                                                }
                                              >
                                                {s.stageName}
                                              </button>
                                            );
                                          })
                                        )}
                                      </div> */}

                                          <div className="checklist555">
                                            {(
                                              todo.toDoListResponseDTOList || []
                                            ).map((li) => {
                                              const isChecked = Boolean(
                                                li?.completion ??
                                                li?.completionFlag ??
                                                (li?.status &&
                                                  String(
                                                    li.status,
                                                  ).toUpperCase() === "DONE"),
                                              );
                                              return (
                                                <label
                                                  key={li.toDoListId ?? li.id}
                                                  className="checklist-item555"
                                                >
                                                  <CheckList
                                                    id={`todo-${todoId}-list-${
                                                      li.toDoListId ?? li.id
                                                    }`}
                                                    label=""
                                                    checked={isChecked}
                                                    disabled={
                                                      !canChecklistUpdate
                                                    }
                                                    onChange={(e) => {
                                                      if (!canChecklistUpdate)
                                                        return;
                                                      const next =
                                                        !!e?.target?.checked;
                                                      onToggleChecklist?.(
                                                        li.toDoListId ?? li.id,
                                                        next,
                                                      );
                                                    }}
                                                  />
                                                  <CustomTooltip
                                                    content={
                                                      li.toDoListTitle || ""
                                                    }
                                                  >
                                                    <span
                                                      className="truncate"
                                                      style={{
                                                        cursor: "pointer",
                                                        flex: 1,
                                                      }}
                                                      onClick={() =>
                                                        openDetailsModal(
                                                          li,
                                                          todo,
                                                        )
                                                      }
                                                    >
                                                      {li.toDoListTitle}
                                                    </span>
                                                  </CustomTooltip>

                                                  <span
                                                    className="info-btn555-wrap"
                                                    role="button"
                                                    tabIndex={0}
                                                    title="Checklist details"
                                                    onClick={(e) => {
                                                      e.preventDefault(); // ⛔ stop label default behavior
                                                      e.stopPropagation(); // ⛔ stop checkbox toggle
                                                      openDetailsModal(
                                                        li,
                                                        todo,
                                                      );
                                                    }}
                                                    onKeyDown={(e) => {
                                                      if (
                                                        e.key === "Enter" ||
                                                        e.key === " "
                                                      ) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        openDetailsModal(
                                                          li,
                                                          todo,
                                                        );
                                                      }
                                                    }}
                                                  >
                                                    <InfoButton />
                                                  </span>

                                                  {/* <TooltipPortal
                                                label="Delete this checklist item"
                                                offsetY={28}
                                              >
                                                <DeleteButton
                                                  size={40}
                                                  onClick={() =>
                                                    onDeleteTodoList?.(
                                                      li.toDoListId ?? li.id
                                                    )
                                                  }
                                                />
                                              </TooltipPortal> */}

                                                  {li.remark ? (
                                                    <span
                                                      className="checklist-item-remark555"
                                                      title={li.remark}
                                                      onClick={(e) =>
                                                        e.stopPropagation()
                                                      }
                                                    >
                                                      Remark: {li.remark}
                                                    </span>
                                                  ) : null}

                                                  <span
                                                    className="checklist-item-actions555"
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                    }}
                                                  >
                                                    <button
                                                      type="button"
                                                      className="checklist-remark-btn"
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        openChecklistRemarkPrompt(
                                                          li,
                                                        );
                                                      }}
                                                    >
                                                      {li.remark
                                                        ? "Edit Remark"
                                                        : "Add Remark"}
                                                    </button>
                                                    {isChecked ? (
                                                      <span className="checklist-approved-badge">
                                                        Approved
                                                      </span>
                                                    ) : (
                                                      <button
                                                        type="button"
                                                        className="checklist-approve-btn"
                                                        onClick={(e) => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          handleApproveChecklistItem(
                                                            li,
                                                          );
                                                        }}
                                                      >
                                                        Approve
                                                      </button>
                                                    )}
                                                  </span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    },
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      }

                      // todo item (compact card) rendered at column level
                      if (colItem.type === "todo") {
                        const todo = colItem.item;
                        const parent = colItem.parent;
                        const todoId = todo.toDoId ?? todo.id;
                        const canChecklistUpdate = can(
                          role,
                          PERM.TODOLIST_UPDATE,
                        );

                        return (
                          <Draggable
                            key={`todo-${todoId}`}
                            draggableId={`todo-${todoId}`}
                            index={index}
                          >
                            {(dragProvided, snapshot) => (
                              <div
                                className={`kanban-task555 ${
                                  snapshot.isDragging ? "dragging" : ""
                                } ${
                                  shakeIds.includes(String(todoId))
                                    ? "shake"
                                    : ""
                                }`}
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                              >
                                <div className="task-header555">
                                  <CustomTooltip
                                    content={todo.toDoTitle || "(no title)"}
                                  >
                                    <span className="title555 truncate">
                                      {todo.toDoTitle}
                                    </span>
                                  </CustomTooltip>
                                  {todo.remark && (
                                    <CustomTooltip content={todo.remark}>
                                      <span
                                        className="todo-remark-icon"
                                        style={{
                                          marginLeft: 6,
                                          fontSize: 12,
                                          color: "#6b7280",
                                          cursor: "help",
                                        }}
                                        title="Has remark"
                                      >
                                        📝
                                      </span>
                                    </CustomTooltip>
                                  )}

                                  <span
                                    className="info-btn555-wrap"
                                    role="button"
                                    tabIndex={0}
                                    title="Details"
                                    onClick={() =>
                                      openDetailsModal(todo, parent)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        openDetailsModal(todo, parent);
                                      }
                                    }}
                                  >
                                    <InfoButton />
                                  </span>
                                </div>

                                <div className="task-meta555">
                                  <CustomTooltip
                                    content={parent?.taskTitle || "—"}
                                  >
                                    <span className="muted555 truncate-row">
                                      <strong>Task:</strong>{" "}
                                      <span className="truncate">
                                        {parent?.taskTitle || "—"}
                                      </span>
                                    </span>
                                  </CustomTooltip>
                                  {todo.assignedEmpName ? (
                                    <TooltipPortal
                                      label={`Assigned: ${todo.assignedEmpName}`}
                                    >
                                      <div
                                        className="avatar555"
                                        data-color={getAvatarColorFor(
                                          todo.assignedEmpId ??
                                            todo.assignedEmpName,
                                        )}
                                      >
                                        {todo.assignedEmpName.charAt(0)}
                                      </div>
                                    </TooltipPortal>
                                  ) : (
                                    <span className="muted555 sm">—</span>
                                  )}

                                  {todo.priority && (
                                    <span
                                      className={`badge555 ${todo.priority.toLowerCase()}`}
                                    >
                                      {todo.priority}
                                    </span>
                                  )}
                                </div>

                                <div className="checklist555">
                                  {(todo.toDoListResponseDTOList || []).map(
                                    (li) => {
                                      const checked = Boolean(
                                        li?.completion ??
                                        li?.completionFlag ??
                                        (li?.status &&
                                          String(li.status).toUpperCase() ===
                                            "DONE"),
                                      );
                                      return (
                                        <label
                                          key={li.toDoListId ?? li.id}
                                          className="checklist-item555"
                                        >
                                          <CheckList
                                            id={`todo-${todoId}-list-${
                                              li.toDoListId ?? li.id
                                            }`}
                                            label=""
                                            checked={checked}
                                            disabled={!canChecklistUpdate}
                                            onChange={(e) => {
                                              if (!canChecklistUpdate) return;
                                              const next = !!e?.target?.checked;
                                              onToggleChecklist?.(
                                                li.toDoListId ?? li.id,
                                                next,
                                              );
                                            }}
                                          />
                                          <CustomTooltip
                                            content={li.toDoListTitle || ""}
                                          >
                                            <span
                                              className="truncate"
                                              style={{
                                                cursor: "pointer",
                                                flex: 1,
                                              }}
                                              onClick={() =>
                                                openDetailsModal(li, todo)
                                              }
                                            >
                                              {li.toDoListTitle}
                                            </span>
                                          </CustomTooltip>
                                          <span
                                            className="info-btn555-wrap"
                                            role="button"
                                            tabIndex={0}
                                            title="Checklist details"
                                            onClick={() =>
                                              openDetailsModal(li, todo)
                                            }
                                            onKeyDown={(e) => {
                                              if (
                                                e.key === "Enter" ||
                                                e.key === " "
                                              ) {
                                                e.preventDefault();
                                                openDetailsModal(li, todo);
                                              }
                                            }}
                                          >
                                            <InfoButton />
                                          </span>

                                          {/* <TooltipPortal
                                          label="Delete this checklist item"
                                          offsetY={28}
                                        >
                                          <DeleteButton
                                            onClick={() =>
                                              onDeleteTodoList?.(
                                                li.toDoListId ?? li.id
                                              )
                                            }
                                            size={5}
                                          />
                                        </TooltipPortal> */}

                                          {li.remark ? (
                                            <span
                                              className="checklist-item-remark555"
                                              title={li.remark}
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              Remark: {li.remark}
                                            </span>
                                          ) : null}

                                          <span
                                            className="checklist-item-actions555"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                            }}
                                          >
                                            <button
                                              type="button"
                                              className="checklist-remark-btn"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                openChecklistRemarkPrompt(li);
                                              }}
                                            >
                                              {li.remark
                                                ? "Edit Remark"
                                                : "Add Remark"}
                                            </button>
                                            {checked ? (
                                              <span className="checklist-approved-badge">
                                                Approved
                                              </span>
                                            ) : (
                                              <button
                                                type="button"
                                                className="checklist-approve-btn"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  e.stopPropagation();
                                                  handleApproveChecklistItem(
                                                    li,
                                                  );
                                                }}
                                              >
                                                Approve
                                              </button>
                                            )}
                                          </span>
                                        </label>
                                      );
                                    },
                                  )}
                                </div>
                                {can(role, PERM.TODOLIST_UPDATE) && (
                                  <AddButton
                                    size={26}
                                    tooltip="Add checklist item"
                                    ariaLabel={`Add checklist to todo ${todoId}`}
                                    onClick={(e) => {
                                      e.stopPropagation(); // ✅ safety (even if header click is removed)
                                      setCheckModal({
                                        open: true,
                                        toDoId: todoId,
                                      });
                                    }}
                                  />
                                )}
                              </div>
                            )}
                          </Draggable>
                        );
                      }

                      return null;
                    })}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            );
          })}
        </DragDropContext>
      </div>

      {selectedItem && (
        <DetailsModal
          item={selectedItem}
          currentUserRole={currentUserRole}
          currentEmpId={currentEmpId}
          onDeleteTask={onDeleteTask}
          currentUserId={currentEmpId}
          projectMembers={projectMembers}
          onUpdateTodoList={onUpdateTodoList}
          onDeleteTodoList={onDeleteTodoList}
          parentTaskOwnerId={
            selectedItem?.parentTaskOwnerId ??
            selectedItem?.parentOwnerId ??
            selectedItem?.__parent?.taskOwnerId ??
            null
          }
          projectEndDate={projectEndDate}
          onClose={() => setSelectedItem(null)}
          onSave={(item, formData) => {
            if (item.taskId) {
              // optimistic UI update
              setLocalTasks((prev) =>
                prev.map((t) =>
                  String(t.taskId) === String(item.taskId)
                    ? {
                        ...t,
                        taskTitle: formData.title,
                        description: formData.description,
                        remark: formData.remark,
                        priority: formData.priority,
                        startDate: formData.startDate,
                        endDate: formData.endDate,
                        schedulingMode: formData.schedulingMode,
                        durationDays: formData.durationDays,
                        assignedEmpId: formData.assignedEmpId,
                      }
                    : t,
                ),
              );

              return handleUpdateTask(item.taskId, {
                taskTitle: formData.title,
                description: formData.description,
                remark: formData.remark,
                priority: formData.priority,
                startDate: formData.startDate,
                endDate: formData.endDate,
                schedulingMode: formData.schedulingMode,
                durationDays: formData.durationDays,
                assignedEmpId: formData.assignedEmpId,
                accessId: formData.accessId,
              });
            }

            if (item.toDoId) {
              const titleValue = formData.title ?? formData.toDoTitle ?? "";
              return handleUpdateTodo(item.toDoId, {
                toDoTitle: titleValue,
                remark: formData.remark,

                // ✅ ADD THESE
                assignedEmpId: formData.assignedEmpId,
                accessId: formData.accessId,
              });
            }

            if (item.toDoListId) {
              const nextTitle = formData.toDoListTitle ?? formData.title ?? "";
              return onUpdateTodoList?.(item.toDoListId, {
                toDoListTitle: nextTitle,
              });
            }
          }}
        />
      )}

      <AddTodoModal
        open={todoModal.open}
        onClose={() =>
          setTodoModal({ open: false, taskId: null, stagePositionId: null })
        }
        taskId={todoModal.taskId}
        stagePositionId={todoModal.stagePositionId}
        accessId={currentAccessId}
        projectMembers={projectMembers}
        onCreateTodo={onCreateTodo}
      />

      <AddChecklistModal
        open={checkModal.open}
        onClose={() => setCheckModal({ open: false, toDoId: null })}
        toDoId={checkModal.toDoId}
        onCreateTodoList={onCreateTodoList}
      />

      {toast?.show && (
        <Toast
          type={toast.type || "info"}
          message={toast.message || ""}
          onClose={() => setToast({ show: false, message: "", type: "info" })}
        />
      )}
      {stageRemarkPrompt && (
        <div
          className="stage-remark-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cancelStageRemark();
          }}
        >
          <div className="stage-remark-modal">
            <h4>Add a remark</h4>
            <p className="stage-remark-sub">
              {stageRemarkPrompt.kind === "task"
                ? `Moving to "${stageRemarkPrompt.stageName || "new stage"}"`
                : "Updating ToDo stage"}
            </p>
            <textarea
              autoFocus
              rows={3}
              placeholder="Optional remark for this stage change…"
              value={stageRemarkText}
              onChange={(e) => setStageRemarkText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  confirmStageRemark();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelStageRemark();
                }
              }}
            />
            <div className="stage-remark-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={cancelStageRemark}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmStageRemark}
              >
                Save stage
              </button>
            </div>
          </div>
        </div>
      )}
      {checklistRemarkPrompt && (
        <div
          className="stage-remark-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cancelChecklistRemarkPrompt();
          }}
        >
          <div className="stage-remark-modal">
            <h4>
              {checklistRemarkPrompt.completion
                ? "Edit remark"
                : "Add a remark"}
            </h4>
            <p className="stage-remark-sub">
              {checklistRemarkPrompt.title || "Checklist item"}
            </p>
            <textarea
              autoFocus
              rows={3}
              placeholder="Remark for this checklist item…"
              value={checklistRemarkText}
              onChange={(e) => setChecklistRemarkText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  confirmChecklistRemarkPrompt();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelChecklistRemarkPrompt();
                }
              }}
            />
            <div className="stage-remark-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={cancelChecklistRemarkPrompt}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmChecklistRemarkPrompt}
              >
                Save remark
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteTarget && (
        <DeleteWarningModal
          message="Are you sure you want to delete this item? This action cannot be undone."
          type="warning"
          onConfirm={() => {
            const { type, id } = deleteTarget;
            if (type === "task") onDeleteTask?.(id);
            if (type === "todo") onDeleteTodo?.(id);
            if (type === "checklist") onDeleteTodoList?.(id);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}
      <AddStageModal
        open={addStageOpen}
        onClose={() => setAddStageOpen(false)}
        projectId={projectId}
        stages={stages}
        parentSubmit={parentSubmit}
      />
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function formFormCheck(formData, key) {
  try {
    return formData && key in formData ? formData[key] : undefined;
  } catch {
    return undefined;
  }
}

KanbanView.propTypes = {
  tasks: PropTypes.array,
  stages: PropTypes.array,
  onChangeTaskStage: PropTypes.func,
  onChangeTodoStage: PropTypes.func,
  onUpdateTodo: PropTypes.func,
  onUpdateTodoList: PropTypes.func,
  onToggleChecklist: PropTypes.func,
  stageChangeLoading: PropTypes.object,
  projectEndDate: PropTypes.string,
  handleUpdateTask: PropTypes.func,
  handleUpdateTodo: PropTypes.func,
  currentUserRole: PropTypes.string,
  projectMembers: PropTypes.array,
  currentEmpId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentAccessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCreateTodo: PropTypes.func,
  onCreateTodoList: PropTypes.func,
  onDeleteTask: PropTypes.func,
};
